var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion, AnimatePresence } from 'framer-motion';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { cva } from 'class-variance-authority';
import { cn } from 'root/src/client/v2/common/helpers/cn';
export const buttonVariants = cva('inline-flex cursor-pointer appearance-none items-center justify-center gap-2 whitespace-nowrap rounded-md border border-solid border-transparent text-sm font-medium outline-none ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0', {
    variants: {
        variant: {
            default: 'bg-primary text-primary-foreground hover:bg-primary/90',
            brand: 'bg-gradient-brand text-white shadow-sm hover:opacity-90',
            neon: 'bg-gradient-to-r from-neon-purple to-neon-pink text-white transition-shadow hover:shadow-[0_0_30px_-5px_hsl(var(--neon-purple)/0.5)]',
            destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            // Semantic counterpart to `destructive`, for a CTA that marks progress the user has already
            // made (e.g. a dare they have taken on) so it reads apart from the brand-gradient CTAs around
            // it. Black foreground, not white: white on --neon-green is only 2.8:1, black is 7.5:1.
            // Distinct from the `success` prop below, which is a transient post-click confirmation overlay.
            success: 'bg-neon-green text-black shadow-sm hover:bg-neon-green/90',
            outline: 'border border-solid border-border bg-transparent hover:bg-secondary hover:text-foreground',
            secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
            ghost: 'hover:bg-secondary hover:text-foreground',
            link: 'text-primary underline-offset-4 hover:underline',
        },
        size: {
            default: 'h-10 px-4 py-2',
            sm: 'h-9 rounded-md px-3',
            lg: 'h-11 rounded-md px-8',
            icon: 'h-10 w-10',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
export const Button = forwardRef((_a, ref) => {
    var { className, variant, size, asChild = false, loading = false, success = false, error = false, disabled, children, type = 'button' } = _a, props = __rest(_a, ["className", "variant", "size", "asChild", "loading", "success", "error", "disabled", "children", "type"]);
    // asChild defers rendering to the child element, so the loader overlay does not apply there.
    if (asChild) {
        return (<Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
					{children}
				</Slot>);
    }
    const showOverlay = loading || success || error;
    return (<button 
    // eslint-disable-next-line react/button-has-type
    type={type} className={cn(buttonVariants({ variant, size }), 'relative', className, success && 'animate-success-pop pointer-events-none border-neon-green/20 bg-none bg-neon-green/10 duration-300', error && 'border-destructive/20 bg-none bg-destructive/10 duration-300')} ref={ref} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
				<span className={cn('inline-flex items-center gap-2 transition-opacity duration-200', showOverlay ? 'opacity-0' : 'opacity-100')}>{children}</span>
				<AnimatePresence mode="wait">
					{loading && (<motion.span key="loader" className="absolute inset-0 flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }} aria-hidden="true">
							<span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"/>
						</motion.span>)}
					{success && !loading && (<motion.span key="success" className="absolute inset-0 flex items-center justify-center text-neon-green" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.4 }} transition={{ type: 'spring', bounce: 0.45, duration: 0.4 }} aria-hidden="true">
							<CheckIcon />
						</motion.span>)}
					{error && !loading && !success && (<motion.span key="error" className="absolute inset-0 flex items-center justify-center text-destructive" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.4 }} transition={{ type: 'spring', bounce: 0.45, duration: 0.4 }} aria-hidden="true">
							<CloseIcon />
						</motion.span>)}
				</AnimatePresence>
			</button>);
});
Button.displayName = 'Button';
